import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getComparePolicyRoute,
  getPolicyComparison,
  getUtcDateNow,
  PAGE_VIEW_EVENTS,
  trackPolicyDetailsSave,
} from 'helpers';
import styles from './styles/PolicyDetailsContainer.module.scss';
import ContactStrip from 'components/contact-strip';
import RenewalComparison from 'components/renewal-comparison';
import { Back, Button } from 'components/quashed';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';
import { PolicyStatus } from 'constants/policy';
import { DASHBOARD_ROUTE, HOME_ROUTE } from 'constants/routes';
import history from 'helpers/history';
import useTracking from 'hooks/useTracking';
import LifeCovers from './components/LifeCovers';
import {
  ResetSelectedPolicy,
  SetRenewalsByPolicy,
  UpdatePolicy,
  UpdatePolicyScan,
} from 'actions';
import PolicyDetailsDocs from './components/PolicyDetailsDocs';
import PolicyDetailsForm from './components/PolicyDetailsForm';
import PolicyDetailsRenewal from './components/PolicyDetailsRenewal';
import { IPolicy, IPolicyScan } from 'types';
import PolicyDetailsCreditCard from './PolicyDetailsCreditCard';
import PolicyRenewalContainer from './PolicyRenewalContainer';
import PolicyDetailsTitle from './components/PolicyDetailsTitle';
import { isPolicyEditable, isPolicyPurchased } from './utils';
import PartnerPurchaseModal from './components/PartnerPurchaseModal';
import classnames from 'classnames';

const PolicyDetailsContainer = ({
  hideBack,
  policyId,
  policyScanId,
  allowMarketScan,
  employer,
  isCreditCard,
  isOwner,
  pendingRenewal,
  policy,
  policyProvider,
  renewals,
  selectedRenewal,
  isReview,
  showComparison,
  showRenewal,
  user,
}) => {
  useTracking(PAGE_VIEW_EVENTS.PolicyDetails);
  const dispatch = useDispatch();
  const [showPartnerModal, setShowPartnerModal] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(ResetSelectedPolicy());
    };
  }, []);

  useEffect(() => {
    if (policy?.id && !policy?.id.includes('credit-card')) {
      dispatch(SetRenewalsByPolicy(policy));
    }
  }, [policy?.id]);

  // Show partner purchase modal for purchased policies
  useEffect(() => {
    if (policy && isPolicyPurchased(policy)) {
      const timer = setTimeout(() => {
        setShowPartnerModal(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [policy]);

  if (isCreditCard) {
    return <PolicyDetailsCreditCard policyId={policyId || policy?.id} />;
  }

  const updatePolicy = (p: IPolicy, submitted?: boolean) =>
    dispatch(UpdatePolicy(p, submitted));

  const updatePolicyScan = (ps: IPolicyScan) => dispatch(UpdatePolicyScan(ps));

  const handleFormSubmit = (data: IPolicy) => {
    if (policyScanId) {
      data.status = PolicyStatus.ACTIVE;
      history.replace(HOME_ROUTE);
      dispatch(
        UpdatePolicyScan({
          id: policyScanId,
          sk: policy.id,
          purchasedAt: getUtcDateNow(),
        } as IPolicyScan),
      );
    }

    updatePolicy(data, false);
    trackPolicyDetailsSave(data);
  };

  if (selectedRenewal) {
    return (
      <PolicyRenewalContainer
        policy={policy}
        policyProvider={policyProvider}
        selectedRenewal={selectedRenewal}
      />
    );
  }

  return (
    <div className={styles.container}>
      <PartnerPurchaseModal
        open={showPartnerModal}
        policy={policy}
        onClose={() => setShowPartnerModal(false)}
      />

      <PolicyDetailsTitle
        img={policyProvider?.logoUrl}
        policy={policy}
        policyProvider={policyProvider?.name !== 'Other' && policyProvider}
        allowMarketScan={allowMarketScan}
      />

      {!isReview && (
        <PolicyDetailsForm
          employer={employer}
          isOwner={isOwner}
          mini={showRenewal && policy.premium !== 0 && policy.premium}
          policy={policy}
          renewals={renewals}
          onSubmit={handleFormSubmit}
          updatePolicy={updatePolicy}
          showButtons={isPolicyEditable(policy, showRenewal)}
        />
      )}

      {(policy.policyType === INSURANCE_TYPES_NAMES.LIFE ||
        policy.policyType === INSURANCE_TYPES_NAMES.HEALTH) && (
        <LifeCovers employer={employer} policy={policy} user={user} />
      )}

      {policyProvider && (
        <ContactStrip className={styles.contactMobile} {...policyProvider} />
      )}

      <div className={styles.bottom}>
        {showComparison && (
          <RenewalComparison
            className={styles.comparison}
            text="Renewal overview"
            nextPremium={policy.premium}
            nextFrequency={policy.frequency}
            isUpcoming={policy.policyRenewalAdded}
            policyProviderName={policy.policyProviderName}
            {...getPolicyComparison(renewals, policy)}
          />
        )}

        <PolicyDetailsDocs
          disabled={isReview}
          employer={employer}
          isOwner={isOwner}
          pendingRenewal={pendingRenewal}
          policy={policy}
          policyScanId={policyScanId}
          showUploadBtn={!!policyScanId || (isOwner && !showRenewal)}
          user={user}
          updatePolicy={updatePolicy}
          updatePolicyScan={updatePolicyScan}
        />
      </div>

      {showRenewal && (
        <PolicyDetailsRenewal
          className={styles.renewal}
          showMarketScanBtn={allowMarketScan}
          pendingRenewal={pendingRenewal}
          policy={policy}
        />
      )}

      <div
        className={classnames(styles.buttons, showRenewal && styles.showMobile)}
      >
        {!hideBack && (
          <Back
            className={styles.back}
            link={DASHBOARD_ROUTE}
            text="Back to dashboard"
          />
        )}
        {allowMarketScan && (
          <Button
            className={styles.marketScanBtn}
            link={getComparePolicyRoute(policy.id)}
            text="Find better deals"
          />
        )}
      </div>
    </div>
  );
};

export default PolicyDetailsContainer;
